import React, { useState, useRef, useEffect, useMemo } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useMediaQuery } from "@mui/material";

const Dropdown = ({
  label,
  options,
  value,
  onChange,
  icon,
  className = "",
  fullWidthOnMobile = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 640px)");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = useMemo(
    () => options?.find((opt) => opt.value === value),
    [options, value],
  );

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleOptionClick = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const isRightAligned = label === "All Status";

  return (
    <div
      className={`relative ${
        fullWidthOnMobile && isMobile ? "w-full" : ""
      } ${className}`}
      ref={dropdownRef}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={`flex items-center gap-2 px-3 py-2.5 sm:py-2 xl:px-2 xl:py-1.5 bg-white border border-surface rounded-lg text-sm sm:text-xs text-gray-700 
          ${
            fullWidthOnMobile && isMobile
              ? "w-full"
              : "min-w-[120px] xl:min-w-[100px]"
          } justify-between
          active:bg-gray-50 transition-colors duration-200`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {icon && <span className="text-gray-500">{icon}</span>}
        <span>{selectedOption?.name || label}</span>
        <KeyboardArrowDownIcon
          className={`text-xs transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fontSize="small"
        />
      </button>
      {isOpen && (
        <div
          className={`
            absolute top-full mt-1 bg-white border border-surface rounded-lg shadow-lg z-50 
            ${
              isMobile
                ? "w-[calc(100%-0.1rem)] py-3 px-[10px]"
                : "min-w-[220px] py-3 px-[10px]"
            }
            ${isRightAligned ? "right-0 left-auto" : "left-0"}
            animate-fadeIn`}
        >
          {options?.map((option) => (
            <button
              type="button"
              key={option.value}
              onClick={() => handleOptionClick(option.value)}
              className={`w-full px-4 py-3 sm:px-3 sm:py-2 text-left text-sm sm:text-xs flex items-center justify-between mb-1 last:mb-0
                ${
                  value === option.value
                    ? "bg-primary text-white"
                    : "text-content border border-content hover:bg-gray-100 active:bg-gray-50"
                }
                transition-colors duration-200 rounded-lg`}
              role="option"
              aria-selected={value === option.value}
            >
              {option.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Add fade-in animation for the dropdown
const styles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fadeIn {
    animation: fadeIn 0.2s ease-out forwards;
  }
`;

// Add styles to document head
const styleElement = document.createElement("style");
styleElement.textContent = styles;
document.head.appendChild(styleElement);

export default Dropdown;
